import PropTypes from 'prop-types';
import { useController, useFormContext } from 'react-hook-form';
import { BetaTextField } from '@bevy/bevy-design-system';
import styles from './styles.module.less';

/**
 * React Hook From Controlled Text Input
 * @param {string} name
 * @param {boolean} autoFocus
 * @param {string} label
 * @param {string} placeholder
 * @param {string} helperText
 * @param {boolean} srOnlyLabel
 * @param {object} rules
 * @see {Documentation} https://react-hook-form.com/docs/usecontroller
 * @returns {React.Component}
 */
const ControlledTextInput = ({
  name,
  autoFocus,
  label,
  disabled,
  placeholder = '',
  helperText,
  srOnlyLabel,
  maxLength,
  size = 'xsmall',
  rules = { required: false },
  AiControls,
  relatedFields,
  ...props
}) => {
  const { control } = useFormContext();
  const {
    field: textField,
    fieldState: { error },
  } = useController({ name, control, rules });

  const aiProps = AiControls ? {
    suffixLayout: 'expand',
    suffix: (
      <AiControls
        className={styles.borderOverflow}
        value={textField.value || ''}
        name={name}
        relatedFields={relatedFields}
        maxLength={maxLength}
      />
    ),
  } : {};

  return (
    <div className={styles.container}>
      <BetaTextField
        ref={textField.ref}
        autoFocus={autoFocus}
        disabled={disabled}
        size={size}
        maxLength={maxLength}
        name={name}
        label={label}
        placeholder={placeholder}
        onChange={textField.onChange}
        onBlur={textField.onBlur}
        value={textField.value || ''}
        errors={error ? [error?.message] : null}
        helperText={helperText}
        srOnlyLabel={srOnlyLabel}
        dataTestId={name}
        dataHelpId={name}
        {...aiProps}
        {...props}
      />
    </div>
  );
};

ControlledTextInput.propTypes = {
  name: PropTypes.string.isRequired,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  rules: PropTypes.object,
  helperText: PropTypes.string,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'large']),
  relatedFields: PropTypes.arrayOf(PropTypes.string),
  srOnlyLabel: PropTypes.bool,
  AiControls: PropTypes.elementType,
  maxLength: PropTypes.number,
};

export default ControlledTextInput;
